import { AxiosError } from 'axios';
import useSWRMutation from 'swr/mutation';

import {
  TrackEventDescriptionT,
  TrackEventReqT,
  TrackPageActionT,
  TrackPageReqT,
  UseTrackingRespT,
  WithStatusT,
} from '@consumer-frontend/nc-utils';

import {
  FetchersT,
  getPageViewId,
  setPageViewId,
} from '../../lib';

export type TrackingEndpointsT = {
  trackEventUrl: string;
  trackPageUrl: string;
};

export type UseTrackingArgsT = {
  endpoints: TrackingEndpointsT;
  fetchers: FetchersT;
};

export type UseTrackingT<
  PageAction extends TrackPageActionT,
  EventDescription extends TrackEventDescriptionT,
> = {
  currentPageViewId: number;
  isMutating: boolean;
  isMutatingEvent: boolean;
  isMutatingPage: boolean;
  trackEvent: (arg: TrackEventReqT<EventDescription>) => Promise<WithStatusT<UseTrackingRespT> | undefined>;
  trackPage: (arg: TrackPageReqT<PageAction>) => Promise<WithStatusT<UseTrackingRespT> | undefined>;
  error?: AxiosError<{ message: string; }>;
};

export const useTrackingBase = <
  PageAction extends TrackPageActionT,
  EventDescription extends TrackEventDescriptionT
>({ endpoints, fetchers }: UseTrackingArgsT): UseTrackingT<PageAction, EventDescription> => {
  // Create trackPage mutation
  const {
    error: pageError,
    trigger: trackPage,
    isMutating: isMutatingPage,
  } = useSWRMutation<
    WithStatusT<UseTrackingRespT>,
    AxiosError<{ message: string; }>,
    string,
    TrackPageReqT<PageAction>
  >(
    endpoints.trackPageUrl,
    (url: string, { arg }) => fetchers.post(url, { arg }),
    {
      onSuccess: async (resp) => setPageViewId(resp.page_view_id),
      throwOnError: false,
    },
  );

  // Create trackEvent mutation
  const {
    error: eventError,
    trigger: trackEvent,
    isMutating: isMutatingEvent,
  } = useSWRMutation<
    WithStatusT<UseTrackingRespT>,
    AxiosError<{ message: string; }>,
    string,
    TrackEventReqT<EventDescription>
  >(
    endpoints.trackEventUrl,
    async (url: string, { arg }) => {
      // Wrap in Promise.resolve to force a microtask delay, ensuring sessionStorage is updated.
      const argWithPage = { ...arg, page_view_id: await Promise.resolve(getPageViewId()) };

      return fetchers.post(url, { arg: argWithPage });
    },
    { throwOnError: false },
  );

  return {
    currentPageViewId: getPageViewId(),
    error: eventError || pageError,
    isMutating: isMutatingEvent || isMutatingPage,
    isMutatingEvent,
    isMutatingPage,
    trackEvent,
    trackPage,
  };
};
